import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import {
  Activity, Sparkles, DollarSign, Clock, AlertTriangle, RefreshCw, Loader2, Info,
  ShieldAlert, PiggyBank, Check, MessageCircleQuestion,
} from 'lucide-react';
import { fmtNumber } from '../lib/localeUtils';

const INTENT_LABELS = {
  operational: 'Stock / price / supplier / branch',
  clinical: 'Clinical (answered)',
  clinical_redirected: 'Clinical (redirected to pharmacist)',
  financial_blocked: 'Cost/profit asked (blocked)',
  customer_blocked: 'Customer/patient details asked (blocked)',
  general: 'General / workflow',
};

const RANGES = [
  { value: 1, label: 'Today' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const FEATURE_LABELS = {
  drug_interaction: 'Drug Interaction Checker',
  fraud_analysis: 'Fraud Detection',
  duplicate_detection: 'Duplicate Finder',
  prescription_scan: 'Prescription Scanner',
  prescription_interaction_check: 'Rx Interaction Check',
  product_search: 'Product Search',
  ai_reorder: 'AI Reorder',
  demand_forecast: 'Demand Forecast',
  generic_name_resolver: 'Generic Name Resolver',
  pricing_optimizer: 'Pricing Optimizer',
  health_ping: 'Health Ping',
  unknown: 'Uncategorized',
};

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
const fmtNum = (n) => fmtNumber(n || 0);

export default function AIUsagePage() {
  const { user } = useAuth();
  const isOwner = ['super_admin', 'admin'].includes(user?.role);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Staff assistant usage insights ("what are staff asking most")
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // Budget state
  const [budget, setBudget] = useState(null);
  const [budgetForm, setBudgetForm] = useState({ monthly_budget_usd: '', auto_throttle: true });
  const [savingBudget, setSavingBudget] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/ai/usage', { params: { days } });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  }, [days]);

  const loadBudget = useCallback(async () => {
    try {
      const res = await api.get('/ai/budget');
      setBudget(res.data);
      setBudgetForm({
        monthly_budget_usd: res.data.monthly_budget_usd ? String(res.data.monthly_budget_usd) : '',
        auto_throttle: !!res.data.auto_throttle,
      });
    } catch {
      /* no-op */
    }
  }, []);

  useEffect(() => { load(); loadBudget(); }, [load, loadBudget]);

  const loadInsights = useCallback(async () => {
    if (!isOwner) return;
    setInsightsLoading(true);
    try {
      const res = await api.get('/claude/assistant-insights', { params: { days } });
      setInsights(res.data);
    } catch {
      /* non-owners get a 403 here — silently skip, section stays hidden */
    } finally {
      setInsightsLoading(false);
    }
  }, [days, isOwner]);

  useEffect(() => { loadInsights(); }, [loadInsights]);

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      const payload = {
        monthly_budget_usd: Number(budgetForm.monthly_budget_usd || 0),
        auto_throttle: !!budgetForm.auto_throttle,
      };
      const { data } = await api.post('/ai/budget', payload);
      setBudget(data);
      toast.success('Budget saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save budget');
    } finally {
      setSavingBudget(false);
    }
  };

  const acknowledgeAlerts = async () => {
    try {
      await api.post('/ai/budget/acknowledge');
      await loadBudget();
      toast.success('Alerts acknowledged');
    } catch (e) {
      toast.error('Failed to acknowledge alerts');
    }
  };

  const totals = useMemo(() => data?.totals || {}, [data?.totals]);
  const today = data?.today || {};
  const byFeature = data?.by_feature || [];
  const daily = data?.daily || [];
  const failureRate = useMemo(() => {
    if (!totals.calls) return 0;
    return Math.round(((totals.failures || 0) / totals.calls) * 100);
  }, [totals]);

  return (
    <div className="space-y-8" data-testid="ai-usage-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 font-outfit text-3xl font-semibold tracking-tight">
            <Activity className="h-7 w-7 text-primary" />
            AI Usage & Telemetry
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Token consumption, estimated cost, and per-feature AI call breakdown.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[160px]" data-testid="ai-usage-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => (
                <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="ai-usage-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Sparkles className="h-5 w-5" />}
          label={`Calls (${RANGES.find(r => r.value === days)?.label.toLowerCase()})`}
          value={fmtNum(totals.calls)}
          sub={`${fmtNum(totals.successes)} success · ${fmtNum(totals.failures)} failed`}
          testid="ai-usage-calls"
        />
        <StatCard
          icon={<DollarSign className="h-5 w-5" />}
          label="Estimated Cost"
          value={fmtUSD(totals.cost_usd)}
          sub="≈ $0.10/$0.40 per 1M tok (Gemini Flash)"
          accent="emerald"
          testid="ai-usage-cost"
        />
        <StatCard
          icon={<Activity className="h-5 w-5" />}
          label="Tokens (total)"
          value={fmtNum(totals.tokens_total)}
          sub={`${fmtNum(totals.tokens_in)} in · ${fmtNum(totals.tokens_out)} out`}
          testid="ai-usage-tokens"
        />
        <StatCard
          icon={<Clock className="h-5 w-5" />}
          label="Avg Latency"
          value={`${fmtNum(totals.avg_latency_ms)} ms`}
          sub={`${failureRate}% failure rate`}
          accent={failureRate > 10 ? 'rose' : undefined}
          testid="ai-usage-latency"
        />
      </div>

      {/* Budget card */}
      <BudgetCard
        budget={budget}
        form={budgetForm}
        setForm={setBudgetForm}
        onSave={saveBudget}
        saving={savingBudget}
        onAcknowledge={acknowledgeAlerts}
      />

      {/* Today compact strip */}
      <Card data-testid="ai-usage-today-strip">
        <CardContent className="flex items-center justify-between py-4 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Today so far</div>
              <div className="font-semibold">{fmtNum(today.calls)} calls · {fmtNum(today.tokens_total)} tokens · {fmtUSD(today.cost_usd)}</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5" /> Cost is an estimate — exact billing lives in your Universal Key dashboard.
          </div>
        </CardContent>
      </Card>

      {/* Daily chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily AI Usage</CardTitle>
        </CardHeader>
        <CardContent>
          {daily.length === 0 ? (
            <EmptyState message="No AI calls in this range yet." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="currentColor" strokeOpacity={0.4} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="currentColor" strokeOpacity={0.4} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="currentColor" strokeOpacity={0.4} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  formatter={(value, name) => name === 'cost_usd' ? [fmtUSD(value), 'Cost'] : [fmtNum(value), name === 'calls' ? 'Calls' : 'Tokens']}
                />
                <Area yAxisId="left" type="monotone" dataKey="calls" stroke="hsl(var(--primary))" fill="url(#costGrad)" strokeWidth={2} />
                <Area yAxisId="right" type="monotone" dataKey="cost_usd" stroke="hsl(var(--chart-2))" fill="transparent" strokeWidth={1.5} strokeDasharray="4 3" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Feature breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">By Feature</CardTitle>
        </CardHeader>
        <CardContent>
          {byFeature.length === 0 ? (
            <EmptyState message="No feature usage recorded yet." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ai-usage-by-feature">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="py-2 pr-4 font-medium">Feature</th>
                    <th className="py-2 pr-4 font-medium text-right">Calls</th>
                    <th className="py-2 pr-4 font-medium text-right">Tokens</th>
                    <th className="py-2 pr-4 font-medium text-right">Failures</th>
                    <th className="py-2 pl-4 font-medium text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {byFeature.map((row) => {
                    const label = FEATURE_LABELS[row.feature] || row.feature;
                    const share = totals.cost_usd ? Math.round((row.cost_usd / totals.cost_usd) * 100) : 0;
                    return (
                      <tr key={row.feature} className="border-b last:border-0 hover:bg-muted/40 transition-colors">
                        <td className="py-2.5 pr-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{label}</span>
                            {share > 0 && <Badge variant="secondary" className="text-[10px] font-normal">{share}% of cost</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground">{row.feature}</div>
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">{fmtNum(row.calls)}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">{fmtNum(row.tokens_total)}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums">
                          {row.failures > 0 ? <Badge variant="destructive" className="font-normal">{row.failures}</Badge> : <span className="text-muted-foreground">0</span>}
                        </td>
                        <td className="py-2.5 pl-4 text-right font-semibold tabular-nums">{fmtUSD(row.cost_usd)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        {data?.note || 'Token counts and cost are estimates.'}
      </p>

      {/* Staff assistant usage insights — what staff are asking most */}
      {isOwner && (
        <Card data-testid="ai-assistant-insights-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MessageCircleQuestion className="h-4 w-4 text-primary" />
              Staff Assistant — Most Asked About
            </CardTitle>
          </CardHeader>
          <CardContent>
            {insightsLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : !insights || insights.total_questions === 0 ? (
              <EmptyState message="No staff assistant questions in this range yet." />
            ) : (
              <div className="space-y-6">
                <div className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground" data-testid="ai-insights-total">{fmtNum(insights.total_questions)}</span>
                  {' '}questions asked across your pharmacy group in the selected range.
                </div>

                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2">By type</div>
                  <div className="flex flex-wrap gap-2" data-testid="ai-insights-by-intent">
                    {Object.entries(insights.by_intent || {}).map(([intent, count]) => (
                      <Badge key={intent} variant="secondary" className="font-normal">
                        {INTENT_LABELS[intent] || intent}: {fmtNum(count)}
                      </Badge>
                    ))}
                  </div>
                </div>

                {(insights.top_search_terms || []).length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-2">Most asked-about items</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="ai-insights-top-terms">
                        <tbody>
                          {insights.top_search_terms.map((row) => (
                            <tr key={row.term} className="border-b last:border-0">
                              <td className="py-2 pr-4">{row.term}</td>
                              <td className="py-2 pl-4 text-right tabular-nums text-muted-foreground">{fmtNum(row.count)} asks</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {(insights.top_topics || []).length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-2">Topic breakdown</div>
                    <div className="flex flex-wrap gap-2">
                      {insights.top_topics.map((row) => (
                        <Badge key={row.topic} variant="outline" className="font-normal capitalize">
                          {row.topic}: {fmtNum(row.count)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const StatCard = ({ icon, label, value, sub, accent, testid }) => {
  const accentClass =
    accent === 'emerald' ? 'text-emerald-600 bg-emerald-500/10'
    : accent === 'rose' ? 'text-rose-600 bg-rose-500/10'
    : 'text-primary bg-primary/10';
  return (
    <Card data-testid={testid} className="hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${accentClass}`}>
            {icon}
          </div>
        </div>
        <div className="mt-3 text-2xl font-semibold tabular-nums">{value}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
        {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
      </CardContent>
    </Card>
  );
};

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
    <Activity className="h-8 w-8 mb-2 opacity-40" />
    <p className="text-sm">{message}</p>
  </div>
);

const TIER_COLOR = {
  50: 'bg-amber-500',
  80: 'bg-orange-500',
  100: 'bg-rose-500',
};

const BudgetCard = ({ budget, form, setForm, onSave, saving, onAcknowledge }) => {
  if (!budget) return null;
  const hasBudget = Number(budget.monthly_budget_usd) > 0;
  const pct = Math.min(100, Number(budget.pct || 0));
  const over = pct >= 100;
  const barColor = over ? 'bg-rose-500' : pct >= 80 ? 'bg-orange-500' : pct >= 50 ? 'bg-amber-500' : 'bg-primary';
  const unack = budget.unacknowledged_alerts || [];

  return (
    <Card data-testid="ai-budget-card" className={over ? 'border-rose-300' : ''}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <PiggyBank className="h-4 w-4 text-primary" />
          Monthly Budget — {budget.month}
        </CardTitle>
        {over && (
          <Badge variant="destructive" className="gap-1"><ShieldAlert className="h-3 w-3" /> Over Budget</Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {hasBudget ? (
          <>
            <div>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-muted-foreground">
                  ${Number(budget.spent_mtd).toFixed(4)} spent of ${Number(budget.monthly_budget_usd).toFixed(2)}
                </span>
                <span className={`font-semibold ${over ? 'text-rose-600' : ''}`}>{budget.pct}%</span>
              </div>
              <div className="relative h-2.5 w-full rounded-full bg-muted overflow-hidden" data-testid="ai-budget-bar">
                <div
                  className={`h-full ${barColor} transition-all`}
                  style={{ width: `${pct}%` }}
                />
                {/* Threshold markers */}
                {[50, 80].map((t) => (
                  <div
                    key={t}
                    className={`absolute top-0 h-full w-px ${TIER_COLOR[t]} opacity-60`}
                    style={{ left: `${t}%` }}
                    title={`${t}% threshold`}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <MiniStat label="Days left" value={`${budget.days_remaining}`} />
              <MiniStat label="Projected" value={`$${Number(budget.projected_spend).toFixed(4)}`} accent={budget.projected_spend > budget.monthly_budget_usd ? 'rose' : undefined} />
              <MiniStat label="Auto-throttle" value={budget.auto_throttle ? 'On' : 'Off'} />
              <MiniStat label="Tiers alerted" value={(budget.thresholds_alerted || []).length ? budget.thresholds_alerted.join(', ') + '%' : '—'} />
            </div>

            {unack.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 flex items-start gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-medium mb-1">
                    {unack.length} unread budget alert{unack.length > 1 ? 's' : ''}
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    {unack.slice(0, 3).map((a) => (
                      <li key={a.alert_id}>
                        Crossed {a.tier}% — spent ${Number(a.spent_mtd).toFixed(4)} of ${Number(a.budget).toFixed(2)} ({a.pct}%)
                      </li>
                    ))}
                  </ul>
                </div>
                <Button size="sm" variant="outline" onClick={onAcknowledge} data-testid="ai-budget-ack">
                  <Check className="h-3.5 w-3.5 mr-1" /> Acknowledge
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground flex items-start gap-3">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>No budget set — AI usage is unmetered. Set a monthly cap below to get alerts at 50 / 80 / 100% and auto-throttle non-critical features.</span>
          </div>
        )}

        {/* Edit form */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end pt-2 border-t">
          <div>
            <Label htmlFor="budget-input" className="text-xs">Monthly budget (USD)</Label>
            <Input
              id="budget-input"
              type="number"
              step="0.01"
              min="0"
              placeholder="0 = unlimited"
              value={form.monthly_budget_usd}
              onChange={(e) => setForm({ ...form, monthly_budget_usd: e.target.value })}
              data-testid="ai-budget-amount"
            />
          </div>
          <div className="flex items-center gap-2 pb-1">
            <Switch
              id="auto-throttle"
              checked={form.auto_throttle}
              onCheckedChange={(v) => setForm({ ...form, auto_throttle: v })}
              data-testid="ai-budget-throttle"
            />
            <Label htmlFor="auto-throttle" className="text-xs cursor-pointer select-none">
              Auto-throttle non-critical features at 100%
            </Label>
          </div>
          <Button onClick={onSave} disabled={saving} data-testid="ai-budget-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Clinical-safety features (drug interaction, prescription scan) always stay enabled regardless of budget.
        </p>
      </CardContent>
    </Card>
  );
};

const MiniStat = ({ label, value, accent }) => (
  <div className="rounded-md bg-muted/40 px-3 py-2">
    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
    <div className={`text-sm font-semibold ${accent === 'rose' ? 'text-rose-600' : ''}`}>{value}</div>
  </div>
);
